import { useState } from 'react';

export default function CategoriesGrid({ categories, onEdit, onDelete, onReorder }) {
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDragStart = (e, category) => {
    setDraggedItem(category);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (!draggedItem) return;

    const dragIndex = categories.findIndex(c => c._id === draggedItem._id);
    if (dragIndex === dropIndex) {
      setDraggedItem(null);
      setDragOverIndex(null);
      return;
    }

    const newCategories = [...categories];
    newCategories.splice(dragIndex, 1);
    newCategories.splice(dropIndex, 0, draggedItem);

    onReorder(newCategories);
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  if (categories.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-12 text-center">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-folder-open-line text-4xl text-gray-400"></i>
        </div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">No categories found</h3>
        <p className="text-gray-500 text-sm">Add your first category to get started</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {categories.map((category, index) => (
        <div
          key={category._id}
          draggable
          onDragStart={(e) => handleDragStart(e, category)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, index)}
          onDragEnd={handleDragEnd}
          className={`bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-200 cursor-move hover:shadow-md ${
            dragOverIndex === index ? 'ring-2 ring-[#EF579A] scale-105' : ''
          } ${draggedItem?._id === category._id ? 'opacity-50' : ''}`}
        >
          {/* Image */}
          <div className="relative aspect-square w-full h-48 overflow-hidden bg-gray-100">
            <img
              src={category.image}
              alt={category.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute top-3 right-3 flex gap-2">
              <button
                onClick={() => onEdit(category)}
                className="w-8 h-8 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center hover:bg-white transition-colors cursor-pointer"
                title="Edit category"
              >
                <i className="ri-edit-line text-gray-700"></i>
              </button>
              <button
                onClick={() => onDelete(category)}
                className="w-8 h-8 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center hover:bg-red-50 transition-colors cursor-pointer"
                title="Delete category"
              >
                <i className="ri-delete-bin-line text-red-600"></i>
              </button>
            </div>
            <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-md">
              <i className="ri-draggable text-gray-600 text-sm"></i>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            <h3 className="font-semibold text-gray-800 text-lg mb-2">
              {category.name}
            </h3>
            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <span className="text-xs text-gray-400">ID: {category._id}</span>
              <button
                onClick={() => onEdit(category)}
                className="text-[#EF579A] text-sm font-medium hover:underline cursor-pointer whitespace-nowrap"
              >
                Edit Details
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
